import * as React from 'react';
import { useState, useRef, useEffect } from 'react';
import Divider from './Divider';
import {
    Box,
    Container,
    Typography,
    Dialog,
    DialogContent,
    IconButton,
    Button,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import { FONT_FAMILY, app, getImageSrc } from '../constants/constants';
import { useTranslation } from 'react-i18next';

export default function ValuableContentLanding({
    drills = [],
    title = '',
    // buttonText = '',
    buttonLink = app,
    promoModalImage = null,
    promoModalTitle = null,
    promoModalButtonText = null
}) {
    const { t, i18n } = useTranslation();
    const [openDialog, setOpenDialog] = useState(null);
    const [openPromoModal, setOpenPromoModal] = useState(false);
    const videoRef = useRef(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // Usar traducciones por defecto si no se proporcionan
    const modalTitle = promoModalTitle || t('ValuableContentLanding00');
    const modalButtonText = promoModalButtonText || t('ValuableContentLanding01');
    const modalImage = promoModalImage || getImageSrc('promo-practice-planning', i18n.language);

    const handleOpenDialog = (drillId) => {
        setOpenDialog(drillId);
    };

    const handleCloseDialog = () => {
        setOpenDialog(null);
    };

    const currentDrill = drills.find(drill => drill.id === openDialog);

    // Reproducir video automáticamente cuando se abre el diálogo
    useEffect(() => {
        if (openDialog !== null && videoRef.current) {
            videoRef.current.play().catch(error => {
                // Manejar errores de autoplay (algunos navegadores pueden bloquearlo)
                console.log('Error al reproducir automáticamente:', error);
            });
        }
    }, [openDialog]);

    // Abrir modal promocional después de un delay
    useEffect(() => {
        const timer = setTimeout(() => {
            setOpenPromoModal(true);
        }, 5000); // 5 segundos después de cargar

        return () => clearTimeout(timer);
    }, []);

    return (
        <>
            <Box
                component="section"
                sx={{
                    py: { xs: 6, md: 8 },
                    bgcolor: 'white',
                    minHeight: '100vh',
                }}
            >
                <Container maxWidth="lg">
                    {/* Título general */}
                    {title && (
                        <Typography
                            variant="h1"
                            sx={{
                                fontFamily: FONT_FAMILY,
                                fontSize: { xs: '2.5rem', md: '3.5rem' },
                                fontWeight: 900,
                                color: '#1a202c',
                                mt: { xs: 4, md: 6 },
                                mb: { xs: 4, md: 6 },
                                textAlign: 'center',
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                            }}
                        >
                            {title}
                        </Typography>
                    )}

                    {drills.map((drill, index) => (
                        <React.Fragment key={drill.id}>
                            <Box
                                sx={{
                                    mb: { xs: 6, md: 8 },
                                    textAlign: { xs: 'center', md: 'left' }
                                }}
                            >
                                {/* Título */}
                                <Typography
                                    variant="h2"
                                    sx={{
                                        fontFamily: FONT_FAMILY,
                                        fontSize: { xs: '1.75rem', md: '2.5rem' },
                                        fontWeight: 800,
                                        color: '#1a202c',
                                        mb: 2,
                                    }}
                                >
                                    {drill.title}
                                </Typography>

                                {/* Descripción */}
                                <Typography
                                    variant="body1"
                                    sx={{
                                        fontFamily: FONT_FAMILY,
                                        fontSize: { xs: '1rem', md: '1.1rem' },
                                        color: '#4a5568',
                                        lineHeight: 1.6,
                                        mb: 4,
                                        maxWidth: { xs: '100%', md: '80%' },
                                        mx: { xs: 'auto', md: 0 }
                                    }}
                                >
                                    {drill.description}
                                </Typography>

                                {/* Imagen con icono de play */}
                                <Box
                                    sx={{
                                        position: 'relative',
                                        width: '100%',
                                        maxWidth: { xs: '100%', md: '400px' },
                                        mx: { xs: 'auto', md: 0 },
                                        cursor: drill.videoUrl ? 'pointer' : 'default',
                                        '&:hover .play-icon': drill.videoUrl ? {
                                            transform: 'translate(-50%, -50%) scale(1.1)',
                                            opacity: 0.9,
                                        } : {},
                                        transition: 'transform 0.3s ease',
                                    }}
                                    onClick={() => drill.videoUrl && handleOpenDialog(drill.id)}
                                >
                                    <Box
                                        component="img"
                                        src={drill.image}
                                        alt={drill.title}
                                        sx={{
                                            width: '100%',
                                            height: 'auto',
                                            borderRadius: '8px',
                                            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                                            display: 'block',
                                        }}
                                        onError={(e) => {
                                            // Imagen placeholder si no existe
                                            e.target.src = 'https://via.placeholder.com/800x450?text=Basketball+Drill';
                                        }}
                                    />
                                    {drill.videoUrl && (
                                        <Box
                                            className="play-icon"
                                            sx={{
                                                position: 'absolute',
                                                top: '50%',
                                                left: '50%',
                                                transform: 'translate(-50%, -50%)',
                                                transformOrigin: 'center center',
                                                color: 'white',
                                                fontSize: { xs: '4rem', md: '5rem' },
                                                opacity: 0.85,
                                                transition: 'all 0.3s ease',
                                                pointerEvents: 'none',
                                                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
                                            }}
                                        >
                                            <PlayCircleOutlineIcon sx={{ fontSize: 'inherit' }} />
                                        </Box>
                                    )}
                                </Box>
                            </Box>

                            {/* Divider (excepto después del último ejercicio) */}
                            {index < drills.length - 1 && (
                                <Divider marginTop={4} marginBottom={4} />
                            )}
                        </React.Fragment>
                    ))}
                </Container>
            </Box>

            {/* Dialog para el video */}
            <Dialog
                open={openDialog !== null}
                onClose={handleCloseDialog}
                maxWidth={false}
                fullWidth
                fullScreen={false}
                sx={{
                    '& .MuiDialog-paper': {
                        borderRadius: '8px',
                        bgcolor: '#000',
                        width: { xs: '95%', md: '600px' },
                        maxWidth: { xs: '95%', md: '600px' },
                        margin: { xs: 'auto', md: 'auto' },
                    }
                }}
            >
                <DialogContent
                    sx={{
                        p: 0,
                        position: 'relative',
                        bgcolor: '#000',
                    }}
                >
                    <IconButton
                        onClick={handleCloseDialog}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            zIndex: 1,
                            color: 'white',
                            bgcolor: 'rgba(0, 0, 0, 0.5)',
                            '&:hover': {
                                bgcolor: 'rgba(0, 0, 0, 0.7)',
                            }
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                    {currentDrill && (
                        <Box
                            component="video"
                            ref={videoRef}
                            src={currentDrill.videoUrl}
                            autoPlay
                            muted
                            controls
                            playsInline
                            sx={{
                                width: '100%',
                                height: { xs: 'auto', md: '500px' },
                                maxHeight: { xs: '70vh', md: '500px' },
                                objectFit: 'contain',
                                display: 'block',
                            }}
                        />
                    )}
                </DialogContent>
            </Dialog>

            {/* Modal promocional */}
            <Dialog
                open={openPromoModal}
                onClose={(event, reason) => {
                    // Solo permitir cerrar con el botón X, no con clic fuera ni ESC
                    if (reason === 'backdropClick') {
                        return;
                    }
                }}
                disableEscapeKeyDown
                maxWidth="sm"
                fullWidth
                fullScreen={false}
                sx={{
                    '& .MuiDialog-paper': {
                        borderRadius: '16px',
                        overflow: 'hidden',
                    }
                }}
            >
                <DialogContent
                    sx={{
                        p: 0,
                        position: 'relative',
                    }}
                >
                    <IconButton
                        onClick={() => setOpenPromoModal(false)}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            zIndex: 1,
                            color: 'white',
                            bgcolor: 'rgba(0, 0, 0, 0.5)',
                            '&:hover': {
                                bgcolor: 'rgba(0, 0, 0, 0.7)',
                            }
                        }}
                    >
                        <CloseIcon />
                    </IconButton>

                    {/* Imagen */}
                    <Box
                        component="img"
                        src={modalImage}
                        alt="Practice planning"
                        sx={{
                            width: '100%',
                            height: 'auto',
                            maxHeight: { xs: '50vh', md: '60vh' },
                            objectFit: 'contain',
                            display: 'block',
                        }}
                        onError={(e) => {
                            e.target.src = 'https://via.placeholder.com/600x250?text=Practice+Planning';
                        }}
                    />

                    {/* Contenido del modal */}
                    <Box
                        sx={{
                            p: { xs: 3, md: 4 },
                            textAlign: 'center',
                            bgcolor: 'white',
                        }}
                    >
                        <Typography
                            variant="h4"
                            sx={{
                                fontFamily: FONT_FAMILY,
                                fontSize: { xs: '1.5rem', md: '2rem' },
                                fontWeight: 800,
                                mb: 3,
                            }}
                        >
                            <Box component="span" sx={{ color: '#1a202c' }}>{t('ValuableContentLanding02')}</Box>
                            <Box component="span" sx={{ color: '#d32f2f' }}>{t('ValuableContentLanding03')}</Box>
                            <Box component="span" sx={{ color: '#1a202c' }}>{t('ValuableContentLanding04')}</Box>
                        </Typography>

                        <Typography
                            variant="body2"
                            sx={{
                                fontFamily: FONT_FAMILY,
                                fontSize: { xs: '0.875rem', md: '0.95rem' },
                                color: '#4a5568',
                                mb: 3,
                                lineHeight: 1.6,
                            }}
                        >
                            {t('ValuableContentLanding05')}
                        </Typography>

                        <Button
                            variant="contained"
                            onClick={() => window.location.href = buttonLink}
                            sx={{
                                fontFamily: FONT_FAMILY,
                                fontSize: { xs: '1rem', md: '1.1rem' },
                                fontWeight: 700,
                                px: { xs: 4, md: 5 },
                                py: { xs: 1.5, md: 2 },
                                borderRadius: '50px',
                                bgcolor: '#1976d2',
                                color: 'white',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                                textTransform: 'none',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    bgcolor: '#1565c0',
                                    boxShadow: '0 6px 16px rgba(0, 0, 0, 0.4)',
                                    transform: 'translateY(-2px)',
                                },
                            }}
                        >
                            {modalButtonText}
                        </Button>
                    </Box>
                </DialogContent>
            </Dialog>
        </>
    );
}


